import { Box, Flex } from "@chakra-ui/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import { useLocation, useNavigate } from "react-router-dom";
import { SearchBarV2 } from "./SearchBarV2";
import { SearchDropdown } from "./dropdown/SearchDropdown";
import { useSearchV2, BROWSE_SEGMENTS } from "../hooks/useSearchContext";
import { isChannelContext } from "../types";
import type { SearchQueryResponse } from "../types";
import { useInstantSearch } from "../hooks/useInstantSearch";

function pickBestPath(
  query: string,
  results: SearchQueryResponse | undefined,
): string {
  const q = encodeURIComponent(query);
  if (!results) return `/browse?q=${q}`;

  if (results.series.total > 0) return `/browse?q=${q}`;
  if (results.releases.total > 0) return `/books?q=${q}`;
  if (results.creators.total > 0) return `/creators?q=${q}`;

  return `/browse?q=${q}`;
}

export function SearchBarWithDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { context, commitSearch } = useSearchV2();

  const isBrowsePage = BROWSE_SEGMENTS.has(location.pathname.split("/")[1] ?? "");
  const isExpandedSectionPage = location.pathname.startsWith("/cms/");
  const isInPlaceSearchPage = isBrowsePage || isExpandedSectionPage;

  const isChannel = isChannelContext(context);
  const isAllContext = !isChannel && context === "all";
  const cmsContext = isChannel ? context.browseKey : context;
  const searchContext = isChannel ? undefined : (context === "all" ? undefined : context);
  const channelFilters = isChannel ? context.filters : undefined;

  const { data: instantResults } = useInstantSearch(
    isExpandedSectionPage ? "" : inputValue.trim(),
    searchContext,
    channelFilters,
  );

  const locationKey = location.pathname + location.search;
  useEffect(() => {
    setIsOpen(false);
    if (isInPlaceSearchPage) {
      const q = new URLSearchParams(location.search).get("q") ?? "";
      setInputValue(q);
    } else {
      setInputValue("");
    }
  }, [locationKey, isInPlaceSearchPage]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      setIsOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const commitAndClose = useCallback(
    (query: string) => {
      setIsOpen(false);
      if (isAllContext) {
        navigate(pickBestPath(query, instantResults));
      } else {
        commitSearch(query);
      }
    },
    [commitSearch, isAllContext, instantResults, navigate],
  );

  const handleSelectSuggestion = useCallback(
    (name: string) => {
      setInputValue(name);
      commitAndClose(name);
    },
    [commitAndClose],
  );

  const isSmall = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();
  const showPanel = isOpen && !isInPlaceSearchPage;

  return (
    <Box ref={wrapperRef} position="relative" w="full">
      {/* Search bar — always in flow, never moves */}
      <Box position="relative" zIndex={showPanel ? 11 : "auto"}>
        <SearchBarV2
          onFocus={handleFocus}
          onChange={handleChange}
        />
      </Box>

      {/* Page overlay when search is open (desktop only) */}
      {showPanel && !isSmall && (
        <Box
          position="fixed"
          inset={0}
          bg="transparent.black.80"
          zIndex={9}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Panel — anchored to this wrapper, expands around and below the search bar */}
      {showPanel && (
        <>
          <Flex
            direction="column"
            position={isSmall ? "fixed" : "absolute"}
            top={isSmall ? `${topBarHeight.get()}px` : "-8px"}
            left={isSmall ? 0 : "-12px"}
            right={isSmall ? 0 : "-12px"}
            bottom={isSmall ? 0 : undefined}
            zIndex={10}
            borderRadius={isSmall ? 0 : "24px"}
            bg="charcoal.charcoal"
            border={isSmall ? "none" : "1px solid"}
            borderColor="transparent.white.20"
            backdropFilter={isSmall ? undefined : "blur(18px)"}
            boxShadow={isSmall ? undefined : "0px 4px 24px 0px rgba(0,0,0,0.4)"}
            overflow={isSmall ? undefined : "hidden"}
            {...(!isSmall && {
              animation: "searchPanelIn 0.15s ease-out",
              sx: {
                "@keyframes searchPanelIn": {
                  "0%": { opacity: 0, transform: "scaleY(0.98)", transformOrigin: "top" },
                  "100%": { opacity: 1, transform: "scaleY(1)" },
                },
              },
            })}
          >
            {/* Spacer for the search bar (which sits above via z-index) */}
            {!isSmall && <Box h="54px" flexShrink={0} mx="12px" mt="8px" />}

            {/* Scrollable content */}
            <Box
              position="relative"
              flex={isSmall ? 1 : undefined}
              minH={isSmall ? 0 : undefined}
              overflowY={isSmall ? "auto" : undefined}
              overscrollBehavior={isSmall ? "contain" : undefined}
            >
              <Box
                className="styled-scrollbar"
                overflowY={isSmall ? undefined : "auto"}
                overscrollBehavior={isSmall ? undefined : "contain"}
                maxH={isSmall ? undefined : "calc(70vh - 70px)"}
                px={isSmall ? "16px" : "20px"}
                pb="32px"
              >
                <SearchDropdown
                  inputValue={inputValue}
                  cmsContext={cmsContext}
                  context={searchContext}
                  channelFilters={channelFilters}
                  onNavigate={handleNavigate}
                  onSelectSuggestion={handleSelectSuggestion}
                />
              </Box>

              {/* Bottom fade gradient */}
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                borderBottomRadius={isSmall ? 0 : "23px"}
                bgGradient="linear(to-b, transparent, charcoal.charcoal)"
                pointerEvents="none"
              />
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );
}
